import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Typography, 
  Tooltip, 
  tableCellClasses, 
  styled, 
  Button, 
  ButtonProps,
  TablePagination,
  Link,
} from '@mui/material';
import AppLayout from '../../layouts/AppLayout';
import { fetchData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import { useNavigate } from 'react-router-dom';
import { purple } from '@mui/material/colors';
import IssueDetailsDialog from './IssueDetailsDialog';
import { useFeatureFlag, useAppConfig } from '../../provider/AppConfigProvider';
import { FORBIDDEN_PATH } from '../../constants';

// Issue type definitions
interface IssueData {
  productTitle?: string;
  qcardId?: string;
  productISBN?: string;
  productModules?: any[];
  qcardTitle?: string;
  description?: string;
  questionDetails?: string;
  qcardTypeNumber?: number;
  qcardTypeAbbreviation?: string;
}

interface Issue {
  issueId: string;
  jiraId: string | null;
  crmCaseId: string;
  dataMasterQuestionId: string;
  issueStatus: string;
  crmSubject: string;
  createdAt: number;
  updatedAt: number;
  dataProductId: string;
  data?: IssueData;
  personEmail?: string;
  personFamilyName?: string;
  personGivenName?: string;
  source?: string;
}

interface IssuePagination {
  pageSize: number;
  totalItems: number;
  total: number;
  hasNext: boolean | string;
  nextToken: string;
  lastEvaluatedKey: string;
}

interface IssueSearchResponse {
  items: Issue[];
  pagination: IssuePagination;
}

const STORAGE_KEY = 'issueListRowsPerPage';

const IssueListContainer: React.FC = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 5;
  });
  const [nextToken, setNextToken] = useState('');
  const [pagination, setPagination] = useState<IssuePagination | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  
  const { initialized: featureFlagsInitialized } = useAppConfig();
  const isIssueListEnabled = useFeatureFlag('issue-list', false);
  const navigate = useNavigate();
  
  // Map to store page number to lastEvaluatedKey mapping (using ref to avoid infinite loops)
  const pageToLastEvaluatedKeyMapRef = useRef<Map<number, string>>(
    new Map([[0, '']]) // Initialize with first page having empty lastEvaluatedKey
  );

  // Common function to search issues
  const getIssuesApi = useCallback(async (): Promise<IssueSearchResponse> => {
    try {
      // Get the lastEvaluatedKey for the current page from the map
      const currentPageLastEvaluatedKey = pageToLastEvaluatedKeyMapRef.current.get(page) || '';
      
      const response = await fetchData(
        `/issues/list?page_size=${rowsPerPage}&page_number=${page}&last_evaluated_key=${currentPageLastEvaluatedKey}`,
        PERMISSIONS.FILTER_ISSUES,
      );
      return response;
    } catch (error) {
      console.error('Error fetching issues:', error);
      throw error;
    }
  }, [page, rowsPerPage]);

  const fetchIssues = useCallback(async () => {
    // Wait for feature flags to be initialized before checking
    if (!featureFlagsInitialized) {
      return;
    }

    setLoading(true);
    setError(null);
    try {

      // check if issue list is enabled
      if (!isIssueListEnabled) {
        navigate(FORBIDDEN_PATH);
        return;
      }

      const response = await getIssuesApi();
      
      // Validate response structure
      if (!response) {
        throw new Error('Invalid response from server');
      }

      // Handle both empty array and null/undefined items
      if (response.items && Array.isArray(response.items)) {
        setIssues(response.items);
        
        if (response.pagination) {
          setPagination(response.pagination);
          // Handle hasNext as both boolean and string
          const hasNext = typeof response.pagination.hasNext === 'boolean' 
            ? response.pagination.hasNext 
            : String(response.pagination.hasNext).toLowerCase() === 'true';
          setHasNextPage(hasNext);
          setHasPreviousPage(page > 0);
          
          // Store the lastEvaluatedKey for the current page in the map
          pageToLastEvaluatedKeyMapRef.current.set(page, response.pagination.lastEvaluatedKey || '');
          
          // Store nextToken for next request
          if (response.pagination.nextToken) {
            setNextToken(response.pagination.nextToken);
          } else {
            setNextToken('');
          }
        } else {
          // If no pagination info, assume no more pages
          setPagination(null);
          setHasNextPage(false);
          setHasPreviousPage(false);
          setNextToken('');
        }
      } else {
        // Empty or invalid items array
        setIssues([]);
        setPagination(response.pagination || null);
        setHasNextPage(false);
        setHasPreviousPage(false);
        setNextToken('');
      }
    } catch (err: any) {
      // Enhanced error handling
      let errorMessage = 'Failed to fetch issues';
      
      if (err?.response?.status) {
        switch (err.response.status) {
          case 401:
            errorMessage = 'Unauthorized. Please check your permissions.';
            break;
          case 403:
            errorMessage = 'Access forbidden. You do not have permission to view issues.';
            break;
          case 404:
            errorMessage = 'Issues endpoint not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.message || `Error ${err.response.status}: Failed to fetch issues`;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error fetching issues:', err);
      
      // Reset state on error
      setIssues([]);
      setPagination(null);
      setHasNextPage(false);
      setHasPreviousPage(false);
      setNextToken('');
    } finally {
      setLoading(false);
    }
  }, [featureFlagsInitialized, getIssuesApi, page, isIssueListEnabled, navigate]);

  // load data on component mount and when pagination parameters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
        fetchIssues();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchIssues]);

  const navigateToIssueDetailsPage = (jiraId: string | null, caseId: string, questionId: string) => {
    // Handle null jiraId - use a placeholder or check if route supports optional jiraId
    if (jiraId) {
      navigate(`/jira-id/${jiraId}/case-id/${caseId}/question-id/${questionId}`);
    } else {
      // If jiraId is null, navigate with a placeholder or handle differently
      // Based on router, jiraId is required, so we'll use 'N/A' or handle it appropriately
      navigate(`/jira-id/N-A/case-id/${caseId}/question-id/${questionId}`);
    }
  }

  const handleIssueIdClick = (issue: Issue) => {
    setSelectedIssue(issue);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedIssue(null);
  };

  // Pagination handlers for MUI TablePagination
  const handleChangePage = (event: unknown, newPage: number) => {
    // Moving to next page - store nextToken for the new page
    if (newPage > page && hasNextPage && nextToken) {
      pageToLastEvaluatedKeyMapRef.current.set(newPage, nextToken);
      setPage(newPage);
    }
    // Moving to previous page - use stored lastEvaluatedKey from map
    else if (newPage < page && hasPreviousPage) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    localStorage.setItem(STORAGE_KEY, newPageSize.toString());
    setPage(0); 
    setNextToken('');
    pageToLastEvaluatedKeyMapRef.current = new Map([[0, '']]);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const ColorButton = styled(Button)<ButtonProps>(({ theme, color }) => ({
    color: theme.palette.getContrastText(purple[500]),
    backgroundColor: color,
    '&:hover': {
      backgroundColor: purple[700],
    },
  }));

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CLOSED':
      case 'CLOSE':
        return 'success';
      case 'OPEN':
        return 'primary';
      default:
        return 'warning';
    }
  };

  return (
    <AppLayout>
      <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mb: 3,
            ml: 3,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Issues Dashboard
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Issue ID</TableCell>
                <TableCell>JIRA ID</TableCell>
                <TableCell>Case ID</TableCell>
                <TableCell>Question ID</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Product Title</TableCell>
                <TableCell>Product Id</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : issues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No issues found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                issues.map((issue, index) => (
                  <TableRow key={issue.issueId || index} hover>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleIssueIdClick(issue)}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.dark',
                          },
                        }}
                      >
                        {issue.issueId || 'N/A'}
                      </Link>
                    </TableCell>
                    <TableCell>{issue.jiraId || 'N/A'}</TableCell>
                    <TableCell>{issue.crmCaseId || 'N/A'}</TableCell>
                    <TableCell>{issue.dataMasterQuestionId || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 300, paddingY: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'wrap' }}>
                      {issue.crmSubject || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, paddingY: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'wrap' }}>
                      {issue.data?.productTitle || 'N/A'}
                    </TableCell>
                    <TableCell>{issue.dataProductId || 'N/A'}</TableCell>
                    <TableCell>{issue.personEmail || 'N/A'}</TableCell>
                    <StyledTableCell>
                      <Box>
                        <Tooltip title="View Issue Details">
                          <ColorButton
                            variant="contained"
                            color={getStatusColor(issue.issueStatus)}
                            style={{
                              borderRadius: 50,
                              padding: '4px 8px',
                              fontSize: '0.625rem',
                            }}
                            size={'small'}
                            onClick={() => navigateToIssueDetailsPage(issue.jiraId, issue.crmCaseId, issue.dataMasterQuestionId)}
                          >
                            {issue.issueStatus}
                          </ColorButton>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                    <TableCell sx={{ py: 6 }}>
                      {issue.createdAt ? new Date(issue.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      }) : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ py: 6 }}>
                      {issue.updatedAt ? new Date(issue.updatedAt).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      }) : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination?.totalItems || -1}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => {
            if (pagination?.total) {
              return `${from}-${to} of ${pagination?.total ? pagination.total : count}`;
            }
            return `${from}-${to} of more than ${to}`;
          }}
          backIconButtonProps={{
            disabled: !hasPreviousPage || loading,
          }}
          nextIconButtonProps={{
            disabled: !hasNextPage || loading,
          }}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      </Box>

      {/* Issue Details Dialog */}
      <IssueDetailsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        issue={selectedIssue}
      />
    </AppLayout>
  );
};

export default IssueListContainer;

